import _ from 'lodash';
import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {ConversationModel} from '../../../models/api/conversationModel';
import {ExternalChannelTypeModel} from '../../../models/api/externalChannelTypeModel';
import {AppState} from '../../appState';
import {AsyncStatus} from '../../appTypes';
import {getChannels} from '../channels/channelsSelectors';

/*
 * Data Selectors.
 */

export function getExternalChannelTypes(state: AppState) {
  return state.data.externalChannelTypes.byId;
}

export const getFlatExternalChannelTypes = createSelector(getExternalChannelTypes, (externalChannelTypes) =>
  _(externalChannelTypes).values().compact().value(),
);

export function getExternalChannelTypesAsyncStatus(state: AppState): AsyncStatus {
  return state.data.externalChannelTypes;
}

/*
 * External channel type selector based on conversation.
 */

interface ConversationSelectorProps {
  conversation?: ConversationModel;
}

export type ConversationSelector<P> = AppParametricSelector<P, ConversationModel | undefined>;

function getConversationFromProps(_state: unknown, props: ConversationSelectorProps) {
  return props.conversation;
}

export type ExternalChannelTypeFromConversation<P> = AppParametricSelector<
  P,
  ExternalChannelTypeModel | undefined
>;

export function makeGetExternalChannelTypeFromConversation(): ExternalChannelTypeFromConversation<ConversationSelectorProps>;
export function makeGetExternalChannelTypeFromConversation<P>(
  getConversation: ConversationSelector<P>,
): ExternalChannelTypeFromConversation<P>;
export function makeGetExternalChannelTypeFromConversation(
  getConversation: ConversationSelector<any> = getConversationFromProps,
) {
  return createSelector(
    [getConversation, getExternalChannelTypes, getChannels],
    (conversation, externalChannelTypes, channels) => {
      if (!conversation) {
        return undefined;
      }

      // Try to find the first channel with an external channel type specified.
      const channelWithExternalType = conversation.channelIds
        .map((channelId) => channels[channelId])
        .find((c) => Boolean(c?.externalChannelTypeId));

      // If the channel does not have an externalChannelTypeId, return early.
      if (!channelWithExternalType || !channelWithExternalType.externalChannelTypeId) {
        return undefined;
      }
      return externalChannelTypes[channelWithExternalType.externalChannelTypeId];
    },
  );
}
